import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import MiniSearch from 'minisearch';

// Loads the static data assets produced by build_site_data.py:
//   /data/submissions.json    primary corpus (each submission carries
//                             cluster_ids + parent_ids — low-level theme
//                             tags and the high-level themes they roll
//                             up to)
//   /data/clusters.json       theme taxonomy at two levels:
//                               { clusters[]:           low-level themes
//                                 parent_clusters[]:    high-level themes
//                                 excluded_from_hierarchy[]: ids
//                                                       intentionally
//                                                       outside the
//                                                       grouping }
//   /data/stats.json          headline numbers for the hero
//   /data/editorial.json      hero copy
//   /data/search_corpus.json  full comment text per doc_id, lazy-loaded
//
// Lazy-loads two things:
//   1. The full comment-text corpus + minisearch index, on first search
//   2. Long comment text for the detail panel, on demand by doc_id

export const DataContext = createContext();
export const useData = () => useContext(DataContext);

const SEARCH_FIELDS = [
  'comment_id',
  'name',
  'inferred_submitter',
  'comment_title',
  'main_recommendation',
  'representative_quote',
  'recommendations_text',
  'comment_text',
  // Synthetic: only joint / multi-stakeholder filings carry the token "joint"
  // here, with a high boost — so a search for "joint" surfaces those filings at
  // the very top (they rarely contain the word "joint" in their own text).
  'joint',
];

const STORE_FIELDS = ['doc_id'];

export const DataProvider = ({ children }) => {
  const [submissions, setSubmissions] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [parentClusters, setParentClusters] = useState([]);
  const [excludedFromHierarchy, setExcludedFromHierarchy] = useState([]);
  const [stats, setStats] = useState(null);
  const [editorial, setEditorial] = useState({ hero_intro: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const searchIndexRef = useRef(null);
  const searchPromiseRef = useRef(null);
  const commentCacheRef = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [subs, cls, st, ed] = await Promise.all([
          fetch('/data/submissions.json').then(r => r.json()),
          fetch('/data/clusters.json').then(r => r.json()),
          fetch('/data/stats.json').then(r => r.json()),
          fetch('/data/editorial.json').then(r => r.json()),
        ]);
        if (cancelled) return;
        setSubmissions(subs);
        // clusters.json is now an object { clusters, parent_clusters,
        // excluded_from_hierarchy }. Older builds wrote a flat array;
        // guard for both shapes so a stale build doesn't crash the app.
        if (Array.isArray(cls)) {
          setClusters(cls);
          setParentClusters([]);
          setExcludedFromHierarchy([]);
        } else {
          setClusters(cls.clusters || []);
          setParentClusters(cls.parent_clusters || []);
          setExcludedFromHierarchy(cls.excluded_from_hierarchy || []);
        }
        setStats(st);
        setEditorial(ed);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const clustersById = useMemo(() => {
    const map = {};
    for (const c of clusters) map[c.id] = c;
    return map;
  }, [clusters]);

  const parentClustersById = useMemo(() => {
    const map = {};
    for (const p of parentClusters) map[p.id] = p;
    return map;
  }, [parentClusters]);

  // Maps low-level cluster id → array of its parent ids.
  // Mirrors the canonical's parent_ids annotation; useful for any UI that
  // wants to group low-level chips by high-level theme. Multi-parent OK —
  // a cluster can appear under multiple parents.
  const parentsByClusterId = useMemo(() => {
    const map = {};
    for (const c of clusters) {
      // Tolerate legacy single-parent payloads (parent_id string) by
      // coercing them into the new array shape.
      const pids = Array.isArray(c.parent_ids)
        ? c.parent_ids
        : (c.parent_id ? [c.parent_id] : []);
      if (pids.length) map[c.id] = pids;
    }
    return map;
  }, [clusters]);

  // Pre-cache inline comment texts so the detail panel never re-fetches them.
  useEffect(() => {
    const cache = commentCacheRef.current;
    for (const s of submissions) {
      if (s.comment_text_inline) cache[s.doc_id] = s.comment_text_inline;
    }
  }, [submissions]);

  // Lazy-build the search index on first call. Subsequent calls return the
  // same Promise so concurrent triggers don't kick off duplicate fetches.
  const ensureSearchIndex = useCallback(async () => {
    if (searchIndexRef.current) return searchIndexRef.current;
    if (searchPromiseRef.current) return searchPromiseRef.current;
    searchPromiseRef.current = (async () => {
      const corpus = await fetch('/data/search_corpus.json').then(r => r.json());
      const docs = submissions.map(s => ({
        doc_id: s.doc_id,
        // The doc_id IS the regulations.gov comment ID (e.g.
        // "ETA-2026-0001-0020"). Index it under its own field so searching
        // the ID surfaces that exact comment. MiniSearch tokenizes on the
        // hyphens, so the query "ETA-2026-0001-0020" matches token-for-token.
        comment_id: s.doc_id || '',
        name: s.name || '',
        // For anonymous comments, the LLM-inferred submitter (org/person) so
        // the comment is findable by that name even though `name` is generic.
        inferred_submitter: s.inferred_submitter?.guess || '',
        // The regulations.gov title field — real text, otherwise unsearchable.
        comment_title: s.comment_title || '',
        main_recommendation: s.main_recommendation || '',
        representative_quote: s.representative_quote || '',
        recommendations_text: (s.recommendations || [])
          .map(r => `${r.title || ''} ${r.details || ''} ${r.justification || ''} ${r.full_text || ''}`)
          .join(' '),
        comment_text: corpus[s.doc_id] || '',
        // Joint filings get a "joint" token so they're findable (and, via the
        // boost below, ranked first) when someone searches "joint".
        joint: s.is_joint ? 'joint' : '',
      }));
      const ms = new MiniSearch({
        idField: 'doc_id',
        fields: SEARCH_FIELDS,
        storeFields: STORE_FIELDS,
        searchOptions: {
          boost: { joint: 12, comment_id: 5, name: 2, inferred_submitter: 2, comment_title: 1.5, main_recommendation: 1.5, representative_quote: 1.2 },
          prefix: true,
          fuzzy: 0.15,
          combineWith: 'AND',
        },
      });
      ms.addAll(docs);
      searchIndexRef.current = ms;
      return ms;
    })();
    return searchPromiseRef.current;
  }, [submissions]);

  // Returns full comment text for a doc, fetching it from /document_text/
  // for long comments that weren't inlined.
  const getCommentText = useCallback(async (docId) => {
    const cache = commentCacheRef.current;
    if (cache[docId]) return cache[docId];
    try {
      const resp = await fetch(`/document_text/${docId}.txt`);
      if (!resp.ok) return null;
      const text = await resp.text();
      cache[docId] = text;
      return text;
    } catch {
      return null;
    }
  }, []);

  const value = {
    submissions,
    clusters,
    clustersById,
    parentClusters,
    parentClustersById,
    parentsByClusterId,
    excludedFromHierarchy,
    stats,
    editorial,
    loading,
    error,
    ensureSearchIndex,
    getCommentText,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
